import { flatten } from "ramda";
import type { FC, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { CardGridArea, formatDateTimeFromISO } from "widgets";
import { toSafeInt } from "widgets/utils";

import { renderSampleAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { buildParentCards, mockAttributeValue } from "@/app-utils/card.js";
import type { Attribute, AttributeValue, Sample } from "@/graphql.js";
import { getUserDisplayName } from "@/modules/users/utils/user.js";
import { getProjectLabel } from "@/redux/modules/config.js";
import { type ISampleState, loadSampleValues } from "@/redux/modules/sample.js";
import type { IAppState } from "@/redux/reducer.js";
import type { ReduxDispatch } from "@/redux/redux-utils.js";
import { ATTRIBUTE_TYPE, COS_ANALYTICAL_ID, COS_ANNOTATION_ID, ENTITY_TYPE } from "@/settings/settings.js";
import { SampleLockedIcon } from "@/ui-modules/core/tiles/components/SampleTile.tsx";

import { ContentPlaceholder } from "../../core/ContentPlaceholder.js";
import { AttributesListWidget } from "../../core/navigation-panel/AttributeWidget.js";
import { SampleMultiValuesCard } from "../cards/SampleMultiValuesCard.js";
import { SampleValuesCard } from "../cards/SampleValuesCard.js";
import GenericEntitySummaryCardView from "../GenericEntitySummaryView.js";

const ANNOTATION_TITLE = "Annotation Values";
const ANALYTICAL_TITLE = "Analytical Values";

export type IAttributeValues = AttributeValue[];

export const SampleDataView: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { sampleId } = useParams<"sampleId">();
  const { sample, isLoading } = useSelector<IAppState, ISampleState>((state) => state.sample);

  // fetch attribute values separately because this fetching can have some performance impact
  const [attributeValues, setAttributeValues] = useState<AttributeValue[][]>([]);
  const [isLoadingApplicableValues, setLoadingApplicableValues] = useState<boolean>(false);
  useEffect(() => {
    if (sampleId) {
      setLoadingApplicableValues(true);
      dispatch(loadSampleValues(toSafeInt(sampleId))).then((attributeValues) => {
        setLoadingApplicableValues(false);
        setAttributeValues(attributeValues);
      });
    }
  }, [sampleId]);

  const showSpinner: boolean = isLoading || isLoadingApplicableValues || !sample;

  const cards = useMemo(() => {
    if (!sample) {
      return null;
    }
    const mapOfAttributeValues = splitAttributeValuesByColumnSetAndParent(sample, attributeValues);
    // Cards for Sample Attributes on the left, cards for the parents on the right
    let cards: ReactNode[] = [];
    for (const name of [ANNOTATION_TITLE, ANALYTICAL_TITLE]) {
      if (mapOfAttributeValues[name].length > 0) {
        cards.push(
          <SampleValuesCard
            key={"svc-saa-" + name}
            sample={sample}
            values={extractApplicableValues(mapOfAttributeValues[name], false)}
            name={name}
            title={name}
            area={CardGridArea.Left}
          />,
          <SampleMultiValuesCard
            key={"svc-msaa-" + name}
            sample={sample}
            values={extractApplicableValues(mapOfAttributeValues[name], true)}
            name={name}
            title={name}
            area={CardGridArea.Left}
          />,
        );
      }
      delete mapOfAttributeValues[name];
    }

    cards = cards.concat(buildParentCards(sample, CardGridArea.Right));

    return cards;
  }, [sample, attributeValues]);

  return (
    <GenericEntitySummaryCardView
      key={"GenericEntitySummaryView"}
      title={sample?.name ?? ""}
      subtitle={sample?.description || "Sample Data"}
      titleTrailer={sample?.locked ? <SampleLockedIcon size={20} /> : null}
      entity={{ id: sample && sample.id, groupType: ENTITY_TYPE.SAMPLE }}
      headerAttributes={[
        <AttributesListWidget attributes={renderSampleAttributesWidgets(sample)} key={"sample-data-view-widget-1"} />,
      ]}
      project={sample?.project}
      isLoading={showSpinner}
    >
      {!cards?.length ? <ContentPlaceholder text={"No data values exist for this sample."} /> : cards}
    </GenericEntitySummaryCardView>
  );
};

function splitAttributeValuesByColumnSetAndParent(
  sample: Sample,
  attributeValues: AttributeValue[][],
): Record<string, AttributeValue[]> {
  const gAtt: Record<string, AttributeValue[]> = {}; // map by name
  // beware: some attributes dont have an att_id !
  gAtt[ANNOTATION_TITLE] = flatten(
    sample.type.sampleAttributes
      .filter((att) => att.columnSet.id === COS_ANNOTATION_ID)
      .map((a: Attribute) => flatten(attributeValues).filter((v: AttributeValue) => v.attribute.id === a.id))
      .filter((values: IAttributeValues) => values.length),
  );
  // put the represented attributes in front of the annotation attributes
  gAtt[ANNOTATION_TITLE] = flatten(generateAttributeValuesForRepresentedAttributes(sample)).concat(
    gAtt[ANNOTATION_TITLE],
  );

  gAtt[ANALYTICAL_TITLE] = flatten(
    sample.type.sampleAttributes
      .filter((att) => att.columnSet.id === COS_ANALYTICAL_ID)
      .map((a: Attribute) => flatten(attributeValues).filter((v: AttributeValue) => v.attribute.id === a.id))
      .filter((values: IAttributeValues) => values.length),
  );

  return gAtt;
}

function extractApplicableValues(attributeValues: AttributeValue[], aligned: boolean) {
  const headAttribute = attributeValues.map((value) => value.attribute).find((attribute) => attribute.isHead);
  if (!headAttribute) {
    return aligned ? [] : attributeValues;
  }
  return attributeValues.filter((a) => (aligned ? isAlignedValue(a) : !isAlignedValue(a)));
}

function isAlignedValue(value: AttributeValue) {
  return value.attribute.multipleValues && value.attribute.groupName;
}

function generateAttributeValuesForRepresentedAttributes(sample: Sample): IAttributeValues[] {
  const rows: IAttributeValues[] = [];
  let count = -100;
  rows.push(mockAttributeValue(count++, ATTRIBUTE_TYPE.FREE_TEXT, "Name", sample.name, "stringValue"));
  if (sample.project) {
    rows.push(
      mockAttributeValue(
        count++,
        ATTRIBUTE_TYPE.FREE_TEXT,
        getProjectLabel(true, false),
        sample.project.name,
        "stringValue",
      ),
    );
  }
  if (sample.type) {
    rows.push(mockAttributeValue(count++, ATTRIBUTE_TYPE.FREE_TEXT, "Type", sample.type.name, "stringValue"));
  }
  rows.push(mockAttributeValue(count++, ATTRIBUTE_TYPE.FREE_TEXT, "Description", sample.description, "stringValue"));
  rows.push(
    mockAttributeValue(
      count++,
      ATTRIBUTE_TYPE.FREE_TEXT,
      "Added",
      formatDateTimeFromISO(sample.created),
      "stringValue",
    ),
  );
  rows.push(mockAttributeValue(count++, ATTRIBUTE_TYPE.FREE_TEXT, "Status", sample.status.name, "stringValue"));
  rows.push(
    mockAttributeValue(
      count++,
      ATTRIBUTE_TYPE.FREE_TEXT,
      "Date Modified",
      formatDateTimeFromISO(sample.modified),
      "stringValue",
    ),
  );
  if (sample.submitter) {
    rows.push(
      mockAttributeValue(
        count++,
        ATTRIBUTE_TYPE.FREE_TEXT,
        "Submitter",
        getUserDisplayName(sample.submitter, false, true),
        "stringValue",
      ),
    );
  }
  if (sample.receiver) {
    rows.push(
      mockAttributeValue(
        count++,
        ATTRIBUTE_TYPE.FREE_TEXT,
        "Receiver",
        getUserDisplayName(sample.receiver, false, true),
        "stringValue",
      ),
    );
  }

  return rows;
}
